import { computed, makeObservable } from 'mobx';

import { hasValue } from 'src/shared/utils/common';
import type { ComparisonStore } from 'src/store/comparison/comparison-store';
import { ImportJob } from 'src/store/jobs/import-job';
import type { JobsStore } from 'src/store/jobs/jobs-store';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import type { PlanVersionsService } from 'src/store/plan-version/plan-versions-service';
import type { RouterStore } from 'src/store/router/router-store';

export class ImportProgressStore {
  private planVersionsService: PlanVersionsService;
  private jobsStore: JobsStore;
  private notifications: NotificationsStore;
  private router: RouterStore;
  private comparison: ComparisonStore;

  constructor(
    planVersionsService: PlanVersionsService,
    jobsStore: JobsStore,
    notifications: NotificationsStore,
    router: RouterStore,
    comparison: ComparisonStore,
  ) {
    this.planVersionsService = planVersionsService;
    this.jobsStore = jobsStore;
    this.notifications = notifications;
    this.router = router;
    this.comparison = comparison;

    makeObservable(this);
  }

  @computed
  get importJobs(): ImportJob[] {
    return this.jobsStore.activeJobs.filter((j) => j instanceof ImportJob);
  }

  @computed
  get currentVersionJob(): ImportJob | null {
    if (!this.importJobs.length) {
      return null;
    }

    const currentVersion = this.planVersionsService.currentPlanVersion;

    if (!currentVersion) {
      console.error('there is no currentVersion');
      return null;
    }

    const currentDraft = this.planVersionsService.currentDraft;

    // Просто дополнительный слой защиты
    if (currentDraft && currentDraft.data.parentVersionId !== currentVersion.id) {
      console.error('current version is not a parent for a current draft');
      return null;
    }

    return (
      this.importJobs.find((job) => {
        const jobImportDraft = this.planVersionsService.draftsList.find((d) => d.id === job.result?.planVersionId);

        if (!jobImportDraft) {
          return false;
        }

        return (
          jobImportDraft?.data.parentVersionId === currentDraft?.id ||
          jobImportDraft?.data.parentVersionId === currentVersion?.id
        );
      }) ?? null
    );
  }

  onGoToImport = async (job: ImportJob): Promise<void> => {
    const importVersionId = job.result?.planVersionId;

    if (!hasValue(importVersionId)) {
      this.notifications.showErrorMessageT('errors:failedToCompleteImport');
      return;
    }

    const isCompletedSuccessfully = job.status === 'COMPLETED' && !job.errorsCount;
    const isCompletedWithError = job.status === 'COMPLETED' && !!job.errorsCount;

    if (isCompletedWithError) {
      await this.planVersionsService.fetchDraftsList();
      this.planVersionsService.setDraft(importVersionId);

      this.router.push(`/carpet/conflicts?planVersionId=${importVersionId}`);
    }

    if (isCompletedSuccessfully) {
      await this.planVersionsService.fetchDraftsList();

      const importPlanVersion = this.planVersionsService.draftsList.find((version) => version.id === importVersionId);
      const currentVersion = this.planVersionsService.currentPlanVersion;

      if (!importPlanVersion || !currentVersion) {
        this.notifications.showErrorMessageT('errors:failedToCompleteImport');
        this.jobsStore.cancelJob(job.id);
        return;
      }

      this.comparison.submitComparingWithImport(currentVersion, importPlanVersion);
    }
  };

  handleCancelImport = (job: ImportJob): void => {
    this.jobsStore.cancelJob(job.id);

    if (job.result?.planVersionId) {
      this.planVersionsService.deleteDraftById(job.result.planVersionId);
    }
  };
}
